/**
 * Standardized error handling utilities
 */

import type { ApiError } from '@/lib/api/apiClient'

/**
 * Application error class for consistent error handling
 */
export class AppError extends Error {
  code: string
  statusCode?: number
  details?: unknown

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    details?: unknown
  ) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.statusCode = statusCode
    this.details = details
    
    // Maintains proper stack trace for where error was thrown
    // captureStackTrace is a Node.js/V8 feature, check if available
    const ErrorConstructor = Error as unknown as {
      captureStackTrace?: (error: Error, constructor: typeof AppError) => void
    }
    if (typeof ErrorConstructor.captureStackTrace === 'function') {
      ErrorConstructor.captureStackTrace(this, AppError)
    }
  }
}

/**
 * Error codes for different error types
 */
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const

/**
 * Transform API error to AppError
 */
export function handleApiError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error
  }

    const apiError = error as ApiError

  if (!apiError) {
    return new AppError(
      'An unexpected error occurred',
      ERROR_CODES.UNKNOWN_ERROR
    )
  }

  // Network errors
  if (!apiError.status) {
    return new AppError(
      apiError.message || 'Network error occurred. Please check your connection.',
      ERROR_CODES.NETWORK_ERROR
    )
  }

  // HTTP status-based errors
  switch (apiError.status) {
    case 400:
      return new AppError(
        apiError.message || 'Invalid request. Please check your input.',
        ERROR_CODES.VALIDATION_ERROR,
        400,
        apiError.errors
      )
    case 401:
      return new AppError(
        apiError.message || 'Authentication required. Please log in.',
        ERROR_CODES.AUTH_ERROR,
        401
      )
    case 403:
      return new AppError(
        apiError.message || 'You do not have permission to perform this action.',
        ERROR_CODES.AUTH_ERROR,
        403
      )
    case 404:
      return new AppError(
        apiError.message || 'The requested resource was not found.',
        ERROR_CODES.NOT_FOUND,
        404
      )
    case 500:
    case 502:
    case 503:
      return new AppError(
        apiError.message || 'Server error. Please try again later.',
        ERROR_CODES.SERVER_ERROR,
        apiError.status
      )
    default:
      return new AppError(
        apiError.message || 'An error occurred',
        ERROR_CODES.UNKNOWN_ERROR,
        apiError.status
      )
  }
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error: AppError | Error | string): string {
  if (typeof error === 'string') {
    return error
  }

  if (error instanceof AppError) {
    return error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred'
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof AppError) {
    return error.code === ERROR_CODES.VALIDATION_ERROR
  }
  return false
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof AppError) {
    return error.code === ERROR_CODES.AUTH_ERROR
  }
  return false
}

function extractFieldMessages(obj: Record<string, unknown>): string[] {
  const parts: string[] = []
  for (const key of Object.keys(obj)) {
    const val = obj[key]
    if (Array.isArray(val)) {
      val.forEach((item) => {
        if (typeof item === 'string' && item.trim()) parts.push(item.trim())
      })
    } else if (typeof val === 'string' && val.trim()) {
      parts.push(val.trim())
    }
  }
  return parts
}

/**
 * Pull a user-facing string from a typical API error body (string, { message/error/reason },
 * { fields: {...} }, { errors: {...} }, or direct field-keyed object).
 */
function messageFromApiBody(responseData: unknown): string | undefined {
  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }
  if (!responseData || typeof responseData !== 'object') {
    return undefined
  }
  const body = responseData as Record<string, unknown>

  // 1. Angular fields object: { fields: { key: ["msg"] } }
  if (body.fields && typeof body.fields === 'object') {
    const parts = extractFieldMessages(body.fields as Record<string, unknown>)
    if (parts.length > 0) return parts.join(' ')
  }

  // 2. Standard Laravel / API errors object: { errors: { field1: ["msg"], field2: ["msg"] } }
  if (body.errors && typeof body.errors === 'object') {
    const parts = extractFieldMessages(body.errors as Record<string, unknown>)
    if (parts.length > 0) return parts.join(' ')
  }

  // 3. Direct field-keyed validation object: { name: ["The name field is required."], email: [...] }
  const metaKeys = new Set(['message', 'error', 'reason', 'status', 'success', 'code', 'data', 'timestamp'])
  const directFieldKeys = Object.keys(body).filter((k) => !metaKeys.has(k))
  if (directFieldKeys.length > 0) {
    const directParts = extractFieldMessages(
      directFieldKeys.reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = body[k]
        return acc
      }, {}),
    )
    if (directParts.length > 0) {
      return directParts.join(' ')
    }
  }

  // 4. Single message / error / reason fields
  if (typeof body.error === 'string' && body.error.trim()) {
    return body.error.trim()
  }
  if (
    body.error &&
    typeof body.error === 'object' &&
    typeof (body.error as { message?: string }).message === 'string' &&
    (body.error as { message: string }).message.trim()
  ) {
    return (body.error as { message: string }).message.trim()
  }
  if (typeof body.reason === 'string' && body.reason.trim()) {
    return body.reason.trim()
  }
  if (typeof body.message === 'string' && body.message.trim()) {
    return body.message.trim()
  }

  return undefined
}

/**
 * Extract error message from various error types
 * @param error - Error object, string, or unknown
 * @param defaultMessage - Default message if error cannot be extracted
 * @returns Error message string
 */
export function extractErrorMessage(error: unknown, defaultMessage: string = 'An error occurred'): string {
  if (typeof error === 'string') {
    return error
  }

  if (error instanceof AppError) {
    return error.message || defaultMessage
  }

  if (error instanceof Error) {
    // Axios errors are Error instances; prefer API body over generic "Request failed with status code …"
    const withResponse = error as Error & {
      response?: { data?: unknown }
    }
    const fromBody = messageFromApiBody(withResponse.response?.data)
    if (fromBody) {
      return fromBody
    }
    return error.message || defaultMessage
  }

  // Handle ApiError type (plain object throws from services / axios interceptor)
  if (error && typeof error === 'object') {
    const apiError = error as {
      message?: string
      data?: unknown
      response?: { data?: unknown }
    }

    const fromResponse = messageFromApiBody(apiError.response?.data)
    if (fromResponse) {
      return fromResponse
    }

    // Interceptor preserves the raw body on `data` (Angular's error.error)
    const fromData = messageFromApiBody(apiError.data)
    if (fromData) {
      return fromData
    }

    if (apiError.message) {
      return apiError.message
    }
  }

  return defaultMessage
}

/**
 * Create an authentication error
 * Used when authentication fails and user should be redirected to login
 * @returns AppError with AUTH_ERROR code
 */
export function createAuthError(): AppError {
  return new AppError(
    'Authentication required. Please log in.',
    ERROR_CODES.AUTH_ERROR,
    401
  )
}
